/**
 * Words for the Facebook history card and the history chat's bar (api/app/fb_history.py codes → Thai for staff).
 * Pure functions: tested in test/fbhistory.spec.ts.
 */
import type { FbHistoryStatus } from '../api/fbhistory'

const TZ = 'Asia/Bangkok'
const fmtDate = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ })
const fmtMonth = new Intl.DateTimeFormat('th-TH', { month: 'short', year: 'numeric', timeZone: TZ })
const fmtClock = new Intl.DateTimeFormat('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ })
const fmtCount = new Intl.NumberFormat('th-TH')

/** "12 ม.ค. 2566" */
export const fullDate = (iso: string): string => fmtDate.format(new Date(iso))
/** "ม.ค. 2566" */
export const monthYear = (iso: string): string => fmtMonth.format(new Date(iso))

/** When the pull continues by itself: "เวลา 05:40 น." today, else "12 ม.ค. 2566 เวลา 05:40 น.". */
export function whenText(iso: string | null, nowMs: number): string {
  if (!iso) return 'ภายหลัง'
  const t = new Date(iso)
  const sameDay = fmtDate.format(t) === fmtDate.format(new Date(nowMs))
  return `${sameDay ? '' : `${fmtDate.format(t)} `}เวลา ${fmtClock.format(t)} น.`
}

/** "58 ข้อความ · ส.ค. 2565 – ก.ย. 2569" */
export function rangeText(h: NonNullable<FbHistoryStatus['history']>): string {
  const n = `${fmtCount.format(h.messages)} ข้อความ`
  if (!h.first_at || !h.last_at) return n
  const a = monthYear(h.first_at)
  const b = monthYear(h.last_at)
  return a === b ? `${n} · ${a}` : `${n} · ${a} – ${b}`
}

export type Tone = 'ok' | 'wait' | 'warn' | 'muted'

export interface CardLine {
  tone: Tone
  text: string
}

const PLATFORM = { fb: 'Facebook', ig: 'Instagram' } as const

export function platformName(s: FbHistoryStatus): string {
  return PLATFORM[s.channel ?? 'fb']
}

/**
 * The one status line of the card (what is happening / why nothing is there). `null` = nothing to add beyond the
 * history summary (a finished pull that reached the first message).
 */
export function statusLine(s: FbHistoryStatus, nowMs: number): CardLine | null {
  const job = s.job ?? null
  const name = platformName(s)
  if (!job) {
    if (s.mode === 'off') return { tone: 'muted', text: 'ปิดการดึงประวัติอยู่ · เปิดได้ที่ ตั้งค่า › ช่องทางที่เชื่อม' }
    if (!s.allowed) return { tone: 'muted', text: 'โหมดทดสอบ: ตอนนี้ดึงประวัติเฉพาะแชทของบัญชีทดสอบ' }
    if (!s.has_token) return { tone: 'warn', text: 'ยังไม่ได้ตั้งสิทธิ์เข้าถึงเพจนี้ในระบบ' }
    return { tone: 'muted', text: `ยังไม่ได้ดึง · จะดึงเองเมื่อลูกค้าทักมาครั้งถัดไป` }
  }
  switch (job.status) {
    case 'queued':
      return { tone: 'wait', text: `รอคิวดึงประวัติจาก ${name}` }
    case 'running':
      return { tone: 'wait', text: job.imported ? `กำลังดึงประวัติ · ได้แล้ว ${fmtCount.format(job.imported)} ข้อความ` : 'กำลังดึงประวัติ…' }
    case 'paused':
      if (job.reason === 'throttled') return { tone: 'wait', text: `${name} ขอให้พักการดึงชั่วคราว จะดึงต่อเอง${whenText(job.next_at, nowMs)}` }
      if (job.reason === 'usage_high')
        return { tone: 'wait', text: `พักไว้ก่อนเพื่อไม่ให้กระทบแชทสด จะดึงต่อเอง${whenText(job.next_at, nowMs)}` }
      if (job.reason === 'mode_off') return { tone: 'muted', text: 'ปิดการดึงประวัติอยู่ · ดึงต่อเมื่อเปิดอีกครั้ง' }
      if (job.reason === 'no_token') return { tone: 'warn', text: 'สิทธิ์เข้าถึงเพจหมดอายุ ต้องต่ออายุ token ของเพจก่อน' }
      return { tone: 'wait', text: `${name} ไม่ตอบ จะลองใหม่เอง${whenText(job.next_at, nowMs)}` }
    case 'failed':
      if (job.reason === 'permission') return { tone: 'warn', text: 'Meta ยังไม่อนุญาตให้ดึงประวัติของลูกค้าคนนี้ (แอปรอผ่าน App Review)' }
      if (job.reason === 'not_allowed') return { tone: 'muted', text: 'โหมดทดสอบ: ตอนนี้ดึงประวัติเฉพาะแชทของบัญชีทดสอบ' }
      if (job.reason === 'list_rejected') return { tone: 'warn', text: `${name} ไม่ให้ดึงรายการข้อความของแชทนี้` }
      return { tone: 'warn', text: 'ดึงประวัติไม่สำเร็จ ลองกดดึงอีกครั้งภายหลังได้' }
    case 'done':
      if (job.reason === 'no_thread') return { tone: 'muted', text: `ไม่พบแชทเก่ากับเพจนี้ · ลูกค้าเพิ่งทักมาครั้งแรก` }
      if (job.reason === 'capped') return { tone: 'muted', text: `ดึงเฉพาะ ${fmtCount.format(job.listed)} ข้อความล่าสุด (สูงสุดต่อคน)` }
      if (!s.history) return { tone: 'muted', text: 'ไม่มีข้อความเก่าที่ต้องดึงเพิ่ม · แชทนี้อยู่ในระบบครบแล้ว' }
      return null
  }
  return null
}

/** Meta refused the older details (its "most recent messages only" rule): say so instead of a silent gap. */
export function refusedLine(s: FbHistoryStatus): string | null {
  const job = s.job
  if (!job || !job.refused) return null
  const before = job.refused_before ? `ก่อน ${fullDate(job.refused_before)} ` : 'ก่อนหน้านี้'
  return `ประวัติ${before}ดึงไม่ได้ — Meta ให้ดึงรายละเอียดเฉพาะข้อความช่วงหลัง`
}

/** Small grey line under the summary: when it was pulled and whether it reached the first message. */
export function doneNote(s: FbHistoryStatus): string | null {
  const job = s.job
  if (!job || job.status !== 'done' || !job.finished_at || !s.history) return null
  return `${job.reached_end ? 'ดึงครบถึงข้อความแรก' : 'ดึงแล้ว'} · ${fullDate(job.finished_at)}`
}

/** The job is still moving: the card asks again every few seconds. */
export function isBusy(s: FbHistoryStatus | null): boolean {
  return !!s?.job && (s.job.status === 'queued' || s.job.status === 'running')
}

/** Label of a click-to-load attachment of an imported chat. */
export function lazyLabel(type: string): string {
  if (type === 'video') return 'วิดีโอเก่าจาก Facebook'
  if (type === 'file') return 'ไฟล์เก่าจาก Facebook'
  return 'รูปเก่าจาก Facebook'
}
